/**
 * The headline figures, as ONE panel divided into columns.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT FOUR CARDS
 *
 * Four separate cards is the default every dashboard reaches for, and it is
 * what made this page read as assembled rather than designed. Each card carries
 * its own border, radius, shadow and padding, so the eye counts six or eight
 * boxes down the page and finds no hierarchy in any of it. Worse, the gaps
 * between the cards are the same size as the gaps between the sections, so
 * nothing groups.
 *
 * These four figures are one thought — how is the company doing this month —
 * and they are read across, not one at a time. So they get one surface and a
 * hairline between columns. One border instead of four, one shadow instead of
 * four, and the row becomes a single object on the page.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY EVERY FIGURE CARRIES A COMPARISON
 *
 * "Revenue MTD · 0.000 BHD" is not information. A number with nothing to
 * measure it against cannot be acted on, which is precisely why an empty
 * dashboard of them looks like a placeholder — it IS one.
 *
 * Each column therefore shows the change against the same window last month,
 * and says which window in words. Where there is genuinely nothing to compare
 * (a first month of trading) it says so rather than printing a meaningless
 * +100%.
 */

import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

export type StatItem = {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** How the absolute change is rendered when a percentage would be noise. */
  formatDelta?: (n: number) => string;
  /** Raw figures, so the delta is computed here rather than in four call sites. */
  current?: number;
  previous?: number;
  /** Up is BAD for this metric — burn, payables, cost. */
  invert?: boolean;
  hint?: ReactNode;
};

/**
 * A percentage change is only meaningful when the base is meaningful.
 *
 * Zero has no percentage change: "+100%" and "+∞%" are both fabrications, and
 * on a young workspace every tile would show one.
 *
 * More subtly, a TINY base produces a technically-correct number that is
 * useless — this dashboard's first run showed "+4,296.9% vs. same period last
 * month" because the same five days last month happened to carry one small
 * invoice. Nobody reads that as growth; they read it as a broken dashboard,
 * which is the exact impression this page was rebuilt to fix.
 *
 * So beyond 999% the change is reported in MONEY instead. "+16,442 BHD" is
 * true, legible, and says the same thing without the spurious precision.
 */
const PCT_CEILING = 999;

function delta(
  current?: number,
  previous?: number,
): { pct: number | null; abs: number; huge: boolean } | null {
  if (typeof current !== 'number' || typeof previous !== 'number') return null;
  const abs = current - previous;
  if (previous === 0) return null;
  const pct = (abs / Math.abs(previous)) * 100;
  return { pct, abs, huge: Math.abs(pct) > PCT_CEILING };
}

export function StatBand({ items, className }: { items: StatItem[]; className?: string }) {
  const { t, lang } = useI18n();
  const locale = lang === 'ar' ? 'ar-BH-u-nu-latn' : 'en';

  return (
    <section
      className={cn(
        'rounded-xl border bg-card shadow-[var(--elev-1)]',
        // The divider follows the writing direction: `divide-x` plus RTL puts
        // the hairline on the correct side without a second rule.
        'grid divide-y sm:grid-cols-2 sm:divide-y-0 xl:grid-cols-4',
        'sm:[&>*+*]:border-s',
        className,
      )}
    >
      {items.map((s) => {
        const d = delta(s.current, s.previous);
        const flat = d !== null && Math.abs(d.pct ?? 0) < 0.05;
        const up = d !== null && d.abs > 0;
        const good = s.invert ? !up : up;

        return (
          <div key={s.key} className="min-w-0 p-5">
            <p className="eyebrow">{s.label}</p>
            <p className="mt-2.5 truncate text-[1.625rem] leading-none font-semibold tracking-tight tabular-nums">
              {s.value}
            </p>

            <div className="mt-3 flex min-h-5 items-center gap-2">
              {d === null ? (
                /* Held open even with nothing in it, so the four columns keep
                   their baselines and the row does not look ragged. */
                <span className="text-[11.5px] text-muted-foreground">{s.hint ?? '—'}</span>
              ) : (
                <>
                  <span
                    className={cn(
                      'inline-flex items-center gap-0.5 text-[11.5px] font-medium tabular-nums',
                      flat
                        ? 'text-muted-foreground'
                        : good
                          ? 'text-success'
                          : 'text-destructive',
                    )}
                  >
                    {flat ? (
                      <Minus className="size-3" aria-hidden />
                    ) : up ? (
                      <ArrowUp className="size-3" aria-hidden />
                    ) : (
                      <ArrowDown className="size-3" aria-hidden />
                    )}
                    {d.huge && s.formatDelta
                      ? s.formatDelta(Math.abs(d.abs))
                      : `${Math.abs(d.pct ?? 0).toLocaleString(locale, { maximumFractionDigits: 1 })}%`}
                  </span>
                  <span className="truncate text-[11.5px] text-muted-foreground">
                    {t('vs_last_month')}
                  </span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
